import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { t } from '../../../i18n'
import { VALID_CLONE_COMPETITION_TYPES } from './constants'
import type { CloneCompetitionType, WizardFormValues } from './types'

dayjs.extend(utc)

/**
 * Normalises an API date to a plain `YYYY-MM-DD` calendar day.
 *
 * Competition start/end dates are pure calendar dates, but the API returns
 * them as midnight-UTC timestamps (e.g. "2026-06-24T00:00:00+00:00"). Feeding
 * that straight into a local-time date picker shifts the displayed day back by
 * one in any timezone behind UTC. Reading the UTC day keeps the form, picker,
 * validation, and payload all on the same calendar day the user sent.
 *
 * Returns null for nullish input so callers can pass API values through
 * directly. Plain "YYYY-MM-DD" input passes through unchanged.
 */
export const toCompetitionDay = (
  value: string | null | undefined
): string | null => (value ? dayjs.utc(value).format('YYYY-MM-DD') : null)

/** Returns the value as a CloneCompetitionType when it's one of the known options, otherwise null. */
export const toCloneCompetitionType = (
  raw: string | null | undefined
): CloneCompetitionType | null =>
  raw && VALID_CLONE_COMPETITION_TYPES.includes(raw as CloneCompetitionType)
    ? (raw as CloneCompetitionType)
    : null

/**
 * Returns the tenant-aware word for "competition" (SportNinja: "competition",
 * Canlan: "season"), capitalised. Use `plural` for the plural form.
 */
export const competitionWord = ({
  plural = false,
  capitalize = true,
}: { plural?: boolean; capitalize?: boolean } = {}): string => {
  const text = t(
    plural ? 'common:competition_plural' : 'common:competition',
    {}
  )
  return capitalize ? t('util:capitalize', { text }) : text
}

/**
 * True when the wizard's Summary submit will route through /schedules/:id/clone
 * instead of /wizard/competition. Enhanced Playoffs always routes through
 * clone (no skip option). Simple Playoffs and Tournament route through clone
 * only when the user picked a source on Step 3 — without a source they walk
 * the regular 6-step new-create flow.
 */
export const isCloneShortCircuit = (values: WizardFormValues): boolean => {
  const type = values.competitionType
  if (type === 'playoffs_enhanced') {
    return true
  }
  if (type === 'playoffs_simple' || type === 'tournament') {
    return Boolean(values.cloneSource.sourceScheduleId)
  }
  return false
}
